import { css } from '@emotion/react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LinearProgress, Stack, Typography } from '@mui/material';
import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { CustomCellEditorProps } from 'ag-grid-react';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { AgTableWithBaseStyle } from '../../../components/AgTable/AgTable2';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { GenericFallbacksWrapper } from '../../../components/Fallback/GenericFallbacksWrapper';
import { FieldFactory } from '../../../components/Form/FieldFactory';
import { LoadingBarPlaceholder } from '../../../components/LoadingBar/LoadingBar';
import { useFocusElement } from '../../../hooks/useFocusElement';
import { projectedFeesAndExpensesTransactionSchema } from '../../../schemas/FundCapitalAllocation.schema';
import { cellEditorWrapperCss, editableGridCss } from '../../../theme/gridStyle';
import { schemaToFormFields } from '../../../util/schema-utils';
import { showProjectionFormState } from '../state/FPState';
import { FPAllocationProps } from './FPAllocation';
import { useFeesAndExpensesFormData } from './feesAndExpensesActions';
import {
  FeesAndExpensesFormData,
  getFeesAndExpensesFormData,
  useFeesAndExpensesFormColDefs,
} from './feesAndExpensesFormData';

const FormGridContainer = css`
  width: 90dvw;
  height: 85dvh;
  max-width: 80rem;
  padding: 0 2.5rem 2rem;
  overflow-y: auto;
  ${editableGridCss};
`;

export function FeesAndExpensesFormModal({ data, fund }: FPAllocationProps) {
  const [showForm, setShowForm] = useAtom(showProjectionFormState);
  function onClose() {
    setShowForm(null);
  }

  if (!showForm || !data || !fund) {
    return null;
  }
  return (
    <GenericFallbacksWrapper suspenseFallback={<></>}>
      <BasicDialog title={'Edit Fees & Expenses'} open={showForm !== null} onClose={onClose}>
        <FeesAndExpensesForm data={data} fund={fund} />
      </BasicDialog>
    </GenericFallbacksWrapper>
  );
}

export function FeesAndExpensesForm({ data, fund }: FPAllocationProps) {
  const formData = useMemo(() => {
    return getFeesAndExpensesFormData(fund, data);
  }, [data, fund]);
  const colDefs = useFeesAndExpensesFormColDefs();
  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data?.period;
  }, []);
  const { onCellValueChange, loading } = useFeesAndExpensesFormData();

  return (
    <>
      <div css={FormGridContainer}>
        <Typography
          variant='body2'
          color='text.secondary'
          display={'flex'}
          alignItems={'center'}
          gap={1}
          mb={'0.5rem'}
        >
          <InfoOutlinedIcon />
          You can edit projected values by double clicking on the cell
        </Typography>
        {loading ? <LinearProgress variant='indeterminate' /> : <LoadingBarPlaceholder />}
        <AgTableWithBaseStyle
          rowData={formData}
          columnDefs={colDefs}
          defaultColDef={defaultColDefs}
          getRowId={getRowId}
          suppressScrollOnNewData={true}
          onCellValueChanged={onCellValueChange}
        />
      </div>
    </>
  );
}

const defaultColDefs: ColDef = {
  flex: 1,
};

export function ProjectedFeesAndExpensesEditor(props: CustomCellEditorProps<FeesAndExpensesFormData>) {
  const formField = useMemo(() => {
    return schemaToFormFields(projectedFeesAndExpensesTransactionSchema(), ['amount']).at(0);
  }, []);
  const { value, onValueChange, stopEditing, data } = props;
  const formProps = {
    onChange: onValueChange,
    onBlur: stopEditing,
    value,
  } as ControllerRenderProps;

  useFocusElement(data?.period, 'input');

  if (formField) {
    return (
      <Stack css={cellEditorWrapperCss} id={data?.period}>
        <FieldFactory formField={formField} formProps={formProps} />
      </Stack>
    );
  }
  return <div>{value}</div>;
}
